import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { EMPLOYEE_ROUTES } from '../../../../common/constants/employee.routing.constants';
import { UserType } from '../../../../common/constants/user-types.constants';
import { RequestTypeEnum } from '../../../../common/enums/request-type';
import { RequestService } from '../../../../common/services/request/request.service';
import { TenantService } from '../../../../common/services/tenant/tenant.service';
import {
  AccessKeyResponse,
  WorkflowAccessKeyService,
} from '../../../../common/services/workflow-access-key/workflow-access-key.service';
import {
  RequestAfterActionContext,
  RequestContextAdapter,
  RequestNavigationTarget,
} from './request-context.adapter';

@Injectable({ providedIn: 'root' })
export class EmployeeRequestContextAdapter implements RequestContextAdapter {
  private readonly tenantService = inject(TenantService);
  private readonly requestService = inject(RequestService);
  private readonly workflowAccessKeyService = inject(WorkflowAccessKeyService);

  getAfterActionNavigation(
    context: RequestAfterActionContext
  ): RequestNavigationTarget {
    if (context.requestType === RequestTypeEnum.CONTRIBUTE) {
      return {
        commands: [
          this.tenantService.tenantPath(EMPLOYEE_ROUTES.MY_INBOX.LIST),
        ],
        queryParams: {
          tab: EMPLOYEE_ROUTES.MY_INBOX.TABS.contributedTasks,
        },
      };
    }

    if (context.userType === UserType.DIRECT_MANAGER) {
      return {
        commands: [
          this.tenantService.tenantPath(EMPLOYEE_ROUTES.MY_TEAM.LIST),
        ],
      };
    }

    if (context.userType === UserType.SUPER_MANAGER) {
      return {
        commands: [
          this.tenantService.tenantPath(EMPLOYEE_ROUTES.MY_ORGANIZATION.LIST),
        ],
      };
    }

    return {
      commands: [this.tenantService.tenantPath(EMPLOYEE_ROUTES.MY_INBOX.LIST)],
    };
  }

  getHomePath(): string {
    return this.tenantService.tenantPath('');
  }

  showCollaboratorsSection(): boolean {
    return true;
  }

  showSaveAction(): boolean {
    return true;
  }

  showRequestViewActions(): boolean {
    return true;
  }

  showHoldResumeActions(): boolean {
    return true;
  }

  getStepAuthorizationKey(
    stepId: string | number,
    userId?: string | number | null
  ): Observable<AccessKeyResponse> {
    return this.workflowAccessKeyService.getStepAuthorizationKey(stepId, userId);
  }

  refreshStepAuthorizationKey(
    stepId: string | number
  ): Observable<{ message: string }> {
    return this.workflowAccessKeyService.refreshStepAuthorizationKey(stepId);
  }

  getFormSection(stepId: string | number): Observable<unknown> {
    return this.requestService.getFormSection(stepId);
  }

  takeStepAction(form: unknown, stepId: string | number): Observable<unknown> {
    return this.requestService.takeStepAction(form, stepId);
  }

  saveRequest(form: unknown, stepId: number): Observable<unknown> {
    return this.requestService.saveRequest(form, stepId);
  }

  getFlowSteps(flowId: string | number): Observable<unknown> {
    return this.requestService.getFlowSteps(flowId);
  }

  getFlowStepsDetails(flowId: string | number): Observable<unknown> {
    return this.requestService.getFlowStepsDetails(flowId);
  }
}
